/**
 * The board picker anchors itself with a `position: fixed` span whose `top` /
 * `left` are written from a trigger's viewport rect. That only lands where it
 * was told to while the span's containing block is the viewport — an ancestor
 * that captures fixed descendants makes the same coordinates resolve against
 * its own box instead, displacing the popover by that box's origin.
 *
 * These helpers find such an ancestor so the caller can subtract it back out.
 */

/** Computed values that mean "this property is not set". */
const isUnset = (value: string | undefined): boolean =>
    !value || value === 'none';

/**
 * `contain` values that capture fixed descendants. `layout` and `paint` each
 * do so on their own; `strict` and `content` are shorthands that include them.
 * `size` and `style` do not, so a `contain: size` ancestor is not one of these.
 */
const CAPTURING_CONTAINMENT = new Set(['layout', 'paint', 'strict', 'content']);

/**
 * `container-type` values that apply layout containment, and with it the
 * containing block. `normal` applies style containment only, and
 * `scroll-state` establishes a scroll-state query container without layout
 * containment — neither captures anything. The value is a list, so
 * `inline-size scroll-state` still counts.
 */
const CAPTURING_CONTAINER_TYPE = new Set(['size', 'inline-size']);

/**
 * Properties that create a containing block when named in `will-change`, per
 * the rule that promoting a property must not change layout once it is set.
 */
const CAPTURING_WILL_CHANGE = new Set([
    'transform',
    'perspective',
    'filter',
    'backdrop-filter',
    'translate',
    'rotate',
    'scale',
]);

/**
 * Whether an element with these computed styles is the containing block for
 * its `position: fixed` descendants.
 *
 * `transform` alone is not enough to decide this:
 *
 * - `translate` / `rotate` / `scale` are separate properties that capture
 *   fixed descendants without appearing in `transform` — and Tailwind v4
 *   emits those rather than a composed `transform`.
 * - `content-visibility: auto` applies layout and paint containment while
 *   leaving the computed value of `contain` at `none`, so it has to be read
 *   directly. This is what put the professional tile's board picker a whole
 *   tile away from its trigger.
 * - A sized container (`container-type: size` or `inline-size`) applies layout
 *   containment for container queries, which brings the containing block with
 *   it.
 */
export const createsFixedContainingBlock = (
    style: CSSStyleDeclaration,
): boolean => {
    if (
        !isUnset(style.transform) ||
        !isUnset(style.perspective) ||
        !isUnset(style.filter) ||
        !isUnset(style.backdropFilter) ||
        !isUnset(style.translate) ||
        !isUnset(style.rotate) ||
        !isUnset(style.scale)
    ) {
        return true;
    }

    const contentVisibility = style.contentVisibility;
    if (contentVisibility === 'auto' || contentVisibility === 'hidden') {
        return true;
    }

    const containerType = style.containerType;
    if (
        containerType &&
        containerType
            .split(/\s+/)
            .some((token) => CAPTURING_CONTAINER_TYPE.has(token))
    ) {
        return true;
    }

    const contain = style.contain;
    if (
        !isUnset(contain) &&
        contain.split(/\s+/).some((token) => CAPTURING_CONTAINMENT.has(token))
    ) {
        return true;
    }

    const willChange = style.willChange;
    if (
        willChange &&
        willChange !== 'auto' &&
        willChange
            .split(',')
            .some((token) => CAPTURING_WILL_CHANGE.has(token.trim()))
    ) {
        return true;
    }

    return false;
};

/**
 * The nearest ancestor that captures `position: fixed` descendants, or null
 * when the element is positioned against the viewport.
 */
export const findFixedContainingBlock = (
    element: HTMLElement,
): HTMLElement | null => {
    let parent = element.parentElement;
    while (parent && parent !== document.documentElement) {
        if (createsFixedContainingBlock(getComputedStyle(parent))) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
};

/**
 * Where to write a fixed element's `top` / `left` so it lands on `targetRect`
 * in viewport coordinates, whatever captures it.
 *
 * Offsets on a fixed element resolve against its containing block's *padding*
 * box, so the ancestor's border is subtracted along with its position.
 *
 * A captor that scales or rotates rather than translating is still only
 * approximated: `getBoundingClientRect` reports its post-transform box while
 * the offsets written here are read pre-transform. Translation — the case
 * carousels produce — is exact.
 */
export const resolveFixedOffset = (
    anchor: HTMLElement,
    targetRect: DOMRect,
): { top: number; left: number } => {
    const containingBlock = findFixedContainingBlock(anchor);
    if (!containingBlock) {
        return { top: targetRect.top, left: targetRect.left };
    }

    const blockRect = containingBlock.getBoundingClientRect();
    const blockStyle = getComputedStyle(containingBlock);
    const borderLeft = parseFloat(blockStyle.borderLeftWidth) || 0;
    const borderTop = parseFloat(blockStyle.borderTopWidth) || 0;

    return {
        top: targetRect.top - blockRect.top - borderTop,
        left: targetRect.left - blockRect.left - borderLeft,
    };
};
